/**
 * main.ts – Einstiegspunkt der Dekalin Demo App
 *
 * Verbindet den LiveAvatar SDK Wrapper (avatar-session.ts)
 * mit dem DOM (index.html) und dem Companion Panel.
 */

import { startAvatarSession, type AvatarSessionHandle } from './avatar-session';
import { type ParseResult, type ProductCard } from './companion-panel/product-links';
import { sendTeamsNotification } from './companion-panel/teams-notify';
import { SessionLogger } from './companion-panel/session-logger';
import { requestConsent } from './companion-panel/consent-banner';

// Versions surfaced into session logs. Keep manually in sync with package.json
// and 20-liveavatar-config/prompt/full-prompt.md when bumping.
const SDK_VERSION = '0.0.16';
const PROMPT_VERSION = '5.0.7';

// Best-effort iframe-origin detection. ancestorOrigins is Chromium/WebKit;
// Safari/iframe falls back to document.referrer; direct page load returns
// the page origin.
function detectIframeOrigin(): string {
  try {
    const ancestor = window.location.ancestorOrigins?.[0];
    if (ancestor) return ancestor;
  } catch {
    // ignore
  }
  if (window.self !== window.top) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return 'iframe-unknown';
    }
  }
  return window.location.origin;
}

// ================================================================
// State
// ================================================================

let session: AvatarSessionHandle | null = null;
let logger: SessionLogger | null = null;
// Last handoff metadata, captured in onHandoff and consumed by submitHandoff
// when the user sends the Teams notification.
let lastHandoffType: 'safety' | 'normal' | 'reklamation' = 'normal';
let lastBrianMessage = '';

// Page-unload safety net: if the user reloads (e.g. "Neue Session" button) or
// closes the tab without hitting our end-button, fire the session log via
// sendBeacon so the audit trail isn't lost. SessionLogger.endSession is
// idempotent — repeated calls are no-ops.
window.addEventListener('beforeunload', () => {
  logger?.endSession('user_ended', /* useBeacon */ true);
});
let chatCount = 0;
let logCount = 0;
let isMicMuted = false;
let isSpeakerMuted = false;
let isAvatarSpeaking = false;
const shownInlineCards = new Set<string>(); // Track cards already shown inline
const chatHistory: Array<{ type: 'avatar' | 'user'; text: string }> = []; // For handoff summary
let typingIndicatorEl: HTMLElement | null = null; // Aktuelle Typing-"..."-Bubble

// ================================================================
// DOM Helpers
// ================================================================

const $ = (id: string) => document.getElementById(id)!;

function time(): string {
  return new Date().toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// ================================================================
// Logging
// ================================================================

function log(level: 'info' | 'success' | 'warn' | 'error', msg: string) {
  const el = document.createElement('div');
  el.className = `log-entry ${level}`;
  el.innerHTML = `<span class="log-time">${time()}</span>${msg}`;
  $('logList').appendChild(el);
  $('logList').scrollTop = $('logList').scrollHeight;
  logCount++;
  $('countLog').textContent = String(logCount);
}

// ================================================================
// Chat
// ================================================================

function addChat(
  text: string,
  type: 'avatar' | 'user',
  cards?: { products: ProductCard[]; blogs: ProductCard[]; videos: ProductCard[] },
) {
  chatHistory.push({ type, text });

  const empty = $('chatEmpty');
  if (empty) empty.remove();

  // Typing-Indikator entfernen sobald die Avatar-Antwort kommt
  if (type === 'avatar') {
    hideTypingIndicator();
  }

  // Beim ersten Avatar-Turn (Begrüßung) das Chat-Panel + Input-Bar einblenden und Input fokussieren.
  // Vorher nimmt der Start-Screen die volle Breite ein (kein leerer Whitespace),
  // und das Avatar-Video bekommt die volle Container-Höhe ohne Platzraub durch die Input-Bar.
  if (type === 'avatar') {
    const main = document.querySelector('.main');
    const wasHidden = !main?.classList.contains('main--active');
    main?.classList.add('main--active');
    if (wasHidden) {
      // Input-Bar erst jetzt einblenden — parallel zum Chat-Panel.
      // Das erhält maximalen Video-Platz in der "Avatar-spricht-Begrüßung"-Phase
      // (wichtig bei knappen iframe-Höhen wie 600px).
      $('userInput').style.display = 'flex';
      // Auto-Focus auf das Eingabefeld nach dem Einblenden (kurz verzögert,
      // damit Layout-Transition nicht unterbrochen wird)
      setTimeout(() => {
        const input = document.getElementById('userTextInput') as HTMLInputElement | null;
        input?.focus();
      }, 450);
    }
  }

  const el = document.createElement('div');
  el.className = `msg ${type}`;

  let html = `<div class="msg-text">${text}</div>`;

  // Inline product/blog/video cards — only show each card ONCE per session
  if (cards) {
    const newProducts = cards.products.filter(p => !shownInlineCards.has(p.key));
    const newBlogs = cards.blogs.filter(b => !shownInlineCards.has(b.key));
    const newVideos = cards.videos.filter(v => !shownInlineCards.has(v.key));

    if (newProducts.length > 0 || newBlogs.length > 0 || newVideos.length > 0) {
      html += '<div class="msg-cards">';
      for (const p of newProducts) {
        shownInlineCards.add(p.key);
        html += `
          <a class="inline-card" href="${p.url}" target="_blank" rel="noopener">
            <span class="inline-card-badge">Produkt</span>
            <strong class="inline-card-name">${p.name}</strong>
            ${p.description ? `<span class="inline-card-desc">${p.description}</span>` : ''}
            <span class="inline-card-link">→ dekalin.de</span>
          </a>`;
      }
      for (const b of newBlogs) {
        shownInlineCards.add(b.key);
        html += `
          <a class="inline-card inline-card--blog" href="${b.url}" target="_blank" rel="noopener">
            <span class="inline-card-badge">Ratgeber</span>
            <strong class="inline-card-name">${b.name}</strong>
            <span class="inline-card-link">→ dekalin.de</span>
          </a>`;
      }
      for (const v of newVideos) {
        shownInlineCards.add(v.key);
        html += `
          <a class="inline-card inline-card--video" href="${v.url}" target="_blank" rel="noopener">
            <span class="inline-card-badge">▶ Video</span>
            <strong class="inline-card-name">${v.name}</strong>
            <span class="inline-card-link">→ How-to ansehen</span>
          </a>`;
      }
      html += '</div>';
    }
  }

  html += `<div class="msg-time">${time()}</div>`;
  el.innerHTML = html;
  $('chat').appendChild(el);
  $('chat').scrollTop = $('chat').scrollHeight;

  chatCount++;
  $('countChat').textContent = String(chatCount);
}

// ================================================================
// Typing-Indikator (3 Pünktchen in Avatar-Bubble)
// ================================================================

function showTypingIndicator() {
  if (typingIndicatorEl) return; // schon sichtbar
  const el = document.createElement('div');
  el.className = 'msg avatar typing';
  el.innerHTML = '<span class="dot"></span><span class="dot"></span><span class="dot"></span>';
  $('chat').appendChild(el);
  $('chat').scrollTop = $('chat').scrollHeight;
  typingIndicatorEl = el;
}

function hideTypingIndicator() {
  if (!typingIndicatorEl) return;
  typingIndicatorEl.remove();
  typingIndicatorEl = null;
}

// ================================================================
// Quick Replies
// ================================================================

const QUICK_REPLY_RULES: Array<{ pattern: RegExp; replies: string[] }> = [
  { pattern: /was steht an|wo fangen wir an|was hast du vor|welches projekt/i, replies: ['Dach abdichten', 'Fenster einbauen', 'Unterboden schützen', 'Wand verkleben'] },
  { pattern: /innen oder außen|drinnen oder draußen|innenseite oder außen/i, replies: ['Innen', 'Außen', 'Beides'] },
  { pattern: /welches material|welcher untergrund|worauf kleb|worauf dicht/i, replies: ['Aluminium', 'GFK', 'Kunststoff', 'Holz'] },
  { pattern: /wohnmobil|wohnwagen|camper|caravan|van/i, replies: ['Dach', 'Seitenwand', 'Fenster', 'Unterboden'] },
  { pattern: /kleben oder dichten|kleben, dichten/i, replies: ['Kleben', 'Dichten', 'Beides'] },
];

let activeQuickReplies: HTMLElement | null = null;

function maybeShowQuickReplies(avatarText: string) {
  // Remove any previous quick replies
  if (activeQuickReplies) {
    activeQuickReplies.remove();
    activeQuickReplies = null;
  }

  for (const rule of QUICK_REPLY_RULES) {
    if (rule.pattern.test(avatarText)) {
      const container = document.createElement('div');
      container.className = 'quick-replies';
      for (const label of rule.replies) {
        const btn = document.createElement('button');
        btn.className = 'quick-reply-btn';
        btn.textContent = label;
        btn.onclick = () => {
          sendUserText(label);
          container.remove();
          activeQuickReplies = null;
        };
        container.appendChild(btn);
      }
      $('chat').appendChild(container);
      $('chat').scrollTop = $('chat').scrollHeight;
      activeQuickReplies = container;
      break; // Only one set of quick replies at a time
    }
  }
}

// ================================================================
// Product Cards
// ================================================================

function renderProducts(products: ProductCard[], blogs: ProductCard[]) {
  const empty = $('productsEmpty');
  if (empty) empty.remove();

  const list = $('productList');
  list.innerHTML = '';

  products.forEach(p => {
    list.innerHTML += `
      <div class="product-card" onclick="window.open('${p.url}','_blank')">
        <div class="card-header">
          <span class="card-name">${p.name}</span>
          <span class="card-badge">Produkt</span>
        </div>
        ${p.description ? `<div class="card-desc">${p.description}</div>` : ''}
        <a class="card-url" href="${p.url}" target="_blank" onclick="event.stopPropagation()">${p.url}</a>
      </div>`;
  });

  blogs.forEach(b => {
    list.innerHTML += `
      <div class="product-card blog" onclick="window.open('${b.url}','_blank')">
        <div class="card-header">
          <span class="card-name">${b.name}</span>
          <span class="card-badge">Ratgeber</span>
        </div>
        <a class="card-url" href="${b.url}" target="_blank" onclick="event.stopPropagation()">${b.url}</a>
      </div>`;
  });

  $('countProducts').textContent = String(products.length + blogs.length);
}

// ================================================================
// Subtitle — DEAKTIVIERT seit UI-Reduktion für iframe-Einbettung
// (Chatverlauf reicht, keine Subtitle-Leiste mehr nötig)
// ================================================================

function showSubtitle(_text: string) {
  // no-op: Subtitle-Bar wurde aus dem DOM entfernt
}

// ================================================================
// Tabs — DEAKTIVIERT seit UI-Reduktion für iframe-Einbettung
// (nur noch Chat-Ansicht, keine Tabs mehr)
// ================================================================

function switchTab(_name: string) {
  // no-op: Tabs wurden aus dem DOM entfernt
}

// ================================================================
// Session Controls
// ================================================================

function updateControlIcons() {
  // Mic icon swap
  const iconMic = $('iconMic');
  const iconMicOff = $('iconMicOff');
  if (iconMic && iconMicOff) {
    iconMic.style.display = isMicMuted ? 'none' : 'block';
    iconMicOff.style.display = isMicMuted ? 'block' : 'none';
  }

  // Speaker icon swap
  const iconSpeaker = $('iconSpeaker');
  const iconSpeakerOff = $('iconSpeakerOff');
  if (iconSpeaker && iconSpeakerOff) {
    iconSpeaker.style.display = isSpeakerMuted ? 'none' : 'block';
    iconSpeakerOff.style.display = isSpeakerMuted ? 'block' : 'none';
  }
}

function toggleMicMute() {
  if (!session || !session.hasMicrophone) return;

  if (isMicMuted) {
    session.unmuteMic();
    isMicMuted = false;
    $('btnMicMute').classList.remove('muted');
    log('info', 'Mikrofon aktiviert');
  } else {
    session.muteMic();
    isMicMuted = true;
    $('btnMicMute').classList.add('muted');
    log('info', 'Mikrofon stummgeschaltet');
  }
  updateControlIcons();
}

function toggleSpeakerMute() {
  const video = $('avatarVideo') as HTMLVideoElement;
  isSpeakerMuted = !isSpeakerMuted;
  video.muted = isSpeakerMuted;
  $('btnSpeakerMute').classList.toggle('muted', isSpeakerMuted);
  log('info', isSpeakerMuted ? 'Lautsprecher stumm' : 'Lautsprecher aktiv');
  updateControlIcons();
}

function interruptAvatar() {
  if (!session || !isAvatarSpeaking) return;

  session.interrupt();
  const btn = $('btnInterrupt');
  btn.classList.add('pulse');
  setTimeout(() => btn.classList.remove('pulse'), 300);
  log('info', 'Avatar unterbrochen');
}

async function endSession() {
  if (!session) return;

  log('info', 'Session wird beendet...');
  // Fire-and-forget — fast UI close. Logger marks itself as ended internally;
  // the beforeunload hook is a no-op after this. We deliberately do NOT null
  // the logger reference here, so any in-flight reload (e.g. "Neue Session"
  // button → location.reload()) still has the beacon fallback available.
  logger?.endSession('user_ended');
  await session.stop();
  session = null;

  // UI zurücksetzen
  $('videoWrapper').style.display = 'none';
  $('userInput').style.display = 'none';
  $('startScreen').style.display = 'flex';
  ($('btnStart') as HTMLButtonElement).disabled = false;
  ($('btnStart') as HTMLButtonElement).textContent = 'Session starten';
  $('statusText').textContent = 'Session beendet';
  // Chat-Panel wieder ausblenden → Start-Screen nimmt volle Breite ein
  document.querySelector('.main')?.classList.remove('main--active');
  log('success', 'Session beendet');

  // State zurücksetzen
  isMicMuted = false;
  isSpeakerMuted = false;
  isAvatarSpeaking = false;
  shownInlineCards.clear();
  chatHistory.length = 0;
}

// ================================================================
// Handoff
// ================================================================

function buildConversationSummary(): string {
  // Take last 6 messages (skip greeting), summarize user intent
  const relevant = chatHistory.filter(m => m.text.length > 10).slice(-6);
  const userMsgs = relevant.filter(m => m.type === 'user').map(m => m.text);
  const avatarMsgs = relevant.filter(m => m.type === 'avatar').map(m => m.text);

  const lines: string[] = [];
  if (userMsgs.length > 0) {
    lines.push(`Kundenanliegen: ${userMsgs.join(' → ')}`);
  }
  // Last avatar message often contains the diagnosis
  if (avatarMsgs.length > 0) {
    const lastAvatar = avatarMsgs[avatarMsgs.length - 1];
    if (lastAvatar.length > 20) {
      lines.push(`Letzte Antwort: ${lastAvatar.substring(0, 150)}${lastAvatar.length > 150 ? '...' : ''}`);
    }
  }
  return lines.join('\n');
}

function showHandoffForm(result: ParseResult) {
  log('warn', '[HANDOFF] erkannt – zeige Kontaktformular');
  $('statusText').textContent = 'Handoff...';

  const box = $('handoffContextBox');
  const parts: string[] = [];

  // Products
  if (session && session.allProducts.length > 0) {
    parts.push(`<strong>Besprochene Produkte:</strong> ${session.allProducts.map(p => p.name).join(', ')}`);
  }

  // Conversation summary
  const summary = buildConversationSummary();
  if (summary) {
    parts.push(`<strong>Gesprächsverlauf:</strong><br>${summary.replace(/\n/g, '<br>')}`);
  }

  if (parts.length > 0) {
    box.innerHTML = parts.join('<br><br>');
    box.classList.add('visible');
  }

  // Pre-fill message field with summary
  const msgField = document.getElementById('hf-message') as HTMLTextAreaElement;
  if (msgField && summary) {
    msgField.value = summary;
  }

  $('handoffOverlay').classList.add('visible');
}

async function submitHandoff(e: Event) {
  e.preventDefault();

  const name = (document.getElementById('hf-name') as HTMLInputElement).value.trim();
  const email = (document.getElementById('hf-email') as HTMLInputElement).value.trim();
  const company = (document.getElementById('hf-company') as HTMLInputElement).value.trim();
  const message = (document.getElementById('hf-message') as HTMLTextAreaElement).value.trim();

  log('info', `Nutzerdaten: ${name} <${email}>${company ? ` (${company})` : ''}`);

  // Read KL Power Automate webhook URL from env. KL's path stays unchanged
  // (DEC-038, parallel-operation strategy). The ASTRAL Graph path is added
  // alongside via /api/handoff below.
  const webhookUrl = (import.meta.env.VITE_TEAMS_WEBHOOK_URL as string | undefined) ?? '';

  // Capture session metadata BEFORE stopping (session.sessionId, startedAt
  // would be inaccessible after session = null otherwise).
  const sessionIdSnapshot = session?.sessionId ?? '';
  const startedAtSnapshot = session ? new Date(session.startedAt).toISOString() : new Date().toISOString();

  // 1. Session stoppen
  if (session) {
    log('info', 'Session wird beendet...');
    await session.stop();
    log('success', 'Session beendet');
  }

  // 2. Handoff-Overlay schließen
  $('handoffOverlay').classList.remove('visible');

  // 3. Video ausblenden, Erfolgsscreen zeigen
  $('videoWrapper').style.display = 'none';
  $('userInput').style.display = 'none';

  const elapsed = session ? Math.floor((Date.now() - session.startedAt) / 1000) : 0;
  const products = session?.allProducts.map(p => p.name).join(', ') || '–';
  $('handoffDetails').innerHTML = `
    Kunde: ${name} &lt;${email}&gt;${company ? ` · ${company}` : ''}<br>
    Produkte: ${products}<br>
    Dauer: ${Math.ceil(elapsed / 60)} Min.
  `;
  $('handoffSuccess').style.display = 'flex';

  log('success', 'Handoff komplett');
  $('statusText').textContent = 'Session beendet (Handoff)';

  // Last user message from chat history — needed for the ASTRAL handover card.
  const lastUserMessage = chatHistory.filter(m => m.type === 'user').pop()?.text ?? '';

  // Parallel notification: KL Power Automate webhook (existing path, untouched)
  // and ASTRAL Graph API via /api/handoff (Block 2). Both fire via
  // Promise.allSettled — failures on either side don't block the other.
  // The ASTRAL path will fail with 500 until RSC consent is granted; KL keeps
  // working in the meantime (DEC-038 — parallel-operation strategy).
  const [klResult, astralResult] = await Promise.allSettled([
    webhookUrl
      ? sendTeamsNotification(webhookUrl, {
          topic: message || '',
          products: products ? products.split(', ') : [],
          summary: message || '',
          duration: elapsed,
          userName: name,
          userEmail: email,
          userCompany: company || '',
        })
      : Promise.resolve(false),
    fetch('/api/handoff', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionIdSnapshot,
        handoff_type: lastHandoffType,
        contact: { name, email, company, message },
        context: {
          last_user_message: lastUserMessage,
          last_brian_message: lastBrianMessage,
          iframe_origin: detectIframeOrigin(),
          user_agent: navigator.userAgent,
          started_at: startedAtSnapshot,
        },
      }),
    }).then(r => r.ok),
  ]);

  log(
    klResult.status === 'fulfilled' && klResult.value ? 'success' : 'warn',
    klResult.status === 'fulfilled' && klResult.value
      ? 'KL Teams-Benachrichtigung gesendet'
      : `KL Teams-Webhook fehlgeschlagen: ${klResult.status === 'rejected' ? klResult.reason : 'response not ok'}`,
  );
  log(
    astralResult.status === 'fulfilled' && astralResult.value ? 'success' : 'warn',
    astralResult.status === 'fulfilled' && astralResult.value
      ? 'ASTRAL Graph-Handoff gesendet'
      : `ASTRAL Graph-Handoff fehlgeschlagen: ${astralResult.status === 'rejected' ? astralResult.reason : 'response not ok'}`,
  );
}

function cancelHandoff() {
  $('handoffOverlay').classList.remove('visible');
  $('statusText').textContent = 'Avatar aktiv';
  log('info', 'Handoff abgebrochen');
}

// ================================================================
// Start Session
// ================================================================

async function startSession() {
  const btn = $('btnStart') as HTMLButtonElement;
  const hint = $('startHint');
  const startAvatar = $('startAvatar');
  const startTitle = $('startTitle');
  const startSubtitle = $('startSubtitle');

  btn.disabled = true;
  btn.textContent = 'Verbinde...';
  btn.style.opacity = '0.5';
  hint.textContent = '';

  // Verbinde-Loader: Start-Avatar bekommt Spinner-Ring, Background dimmt blau, Copy ändert sich
  startAvatar.classList.add('loading');
  $('startScreen').classList.add('loading');
  startTitle.textContent = 'Brian wird geweckt…';
  startSubtitle.textContent = 'Einen Moment, ich verbinde dich mit dem Avatar.';

  log('info', 'Session wird gestartet...');

  // DSGVO opt-in for session logging. Consent decision is persisted in
  // localStorage; modal only appears on first run. Decline → SessionLogger
  // self-disables, nothing leaves the browser.
  const consent = await requestConsent();
  log('info', `Consent: ${consent}`);

  try {
    const video = $('avatarVideo') as HTMLVideoElement;

    session = await startAvatarSession(video, {
      onTranscription: (result: ParseResult) => {
        addChat(result.cleanText, 'avatar', {
          products: result.products,
          blogs: result.blogs,
          videos: result.videos,
        });
        showSubtitle(result.cleanText);
        log('info', `Transkription: "${result.cleanText.substring(0, 60)}..."`);

        // Quick reply suggestions based on avatar text
        maybeShowQuickReplies(result.cleanText);

        if (session) {
          renderProducts(session.allProducts, session.allBlogs);
        }

        logger?.addTurn('brian', result.cleanText, {
          rendered_cards: result.products.map((p) => p.key),
        });
        if (result.products.length > 0) {
          logger?.addProducts(result.products.map((p) => p.key));
        }
      },

      onUserTranscription: (text: string) => {
        addChat(text, 'user');
        log('info', `User (Sprache): "${text.substring(0, 60)}..."`);
        // Avatar wird gleich antworten — Typing-Bubble direkt zeigen
        showTypingIndicator();

        logger?.addTurn('user', text, { input_mode: 'voice' });
      },

      onHandoff: (result: ParseResult) => {
        addChat(result.cleanText, 'avatar', {
          products: result.products,
          blogs: result.blogs,
          videos: result.videos,
        });
        showSubtitle(result.cleanText);
        showHandoffForm(result);

        // Heuristic for handoff classification — refined in Block 2 when the
        // /api/handoff function gets a richer trigger-detection contract.
        const text = result.cleanText.toLowerCase();
        const handoffType: 'safety' | 'normal' | 'reklamation' = /sicherheit|giftnotruf|fachwerkstatt|notruf|112|089\/19240/.test(text)
          ? 'safety'
          : /reklamation|geld zurück|nicht ausgehärtet|reklamier/.test(text)
          ? 'reklamation'
          : 'normal';
        lastHandoffType = handoffType;
        lastBrianMessage = result.cleanText;
        logger?.markHandoff(handoffType);
        logger?.addTurn('brian', result.cleanText, {
          rendered_cards: result.products.map((p) => p.key),
        });
      },

      onSessionEnd: () => {
        log('warn', 'Session beendet (vom Server)');
        $('statusText').textContent = 'Session beendet';
        // Fire-and-forget — UX shouldn't block on the log endpoint
        logger?.endSession('avatar_disconnect');
      },

      onError: (err: Error) => {
        log('error', `Fehler: ${err.message}`);
        $('statusText').textContent = 'Fehler';
        hint.textContent = err.message;
      },

      onStatus: (status: string) => {
        $('statusText').textContent = status;
        log('info', `Status: ${status}`);
      },

      onAvatarSpeakStarted: () => {
        isAvatarSpeaking = true;
        $('btnInterrupt')?.classList.remove('disabled');
      },

      onAvatarSpeakEnded: () => {
        isAvatarSpeaking = false;
        $('btnInterrupt')?.classList.add('disabled');
      },

      // UI sofort umschalten sobald der Stream da ist — passiert i.d.R. mehrere
      // Sekunden bevor `await startAvatarSession()` resolved. Vorher wartete
      // der User auf einem leeren Spinner während configureSession etc. lief.
      onReady: () => {
        $('startScreen').style.display = 'none';
        $('videoWrapper').style.display = 'block';
        log('info', 'Stream bereit – UI umgeschaltet');
      },
    });

    // Init session logger (no-op when consent declined). Lives until endSession()
    // either via stop-button or onSessionEnd.
    logger = new SessionLogger(session.sessionId, {
      promptVersion: PROMPT_VERSION,
      sdkVersion: SDK_VERSION,
      iframeOrigin: detectIframeOrigin(),
    });

    // Mic-Button ausblenden wenn kein Mikrofon
    if (!session.hasMicrophone) {
      $('btnMicMute').style.display = 'none';
    }

    // Falls onReady (Stream-Event) nicht gefeuert hat — Fallback: jetzt umschalten.
    // Sollte selten passieren, aber schützt vor SDK-Edge-Cases.
    if ($('startScreen').style.display !== 'none') {
      $('startScreen').style.display = 'none';
      $('videoWrapper').style.display = 'block';
    }

    log('success', `Session gestartet: ${session.sessionId}`);
    $('statusText').textContent = 'Avatar aktiv';

  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    log('error', `Session-Start fehlgeschlagen: ${error.message}`);
    btn.disabled = false;
    btn.textContent = 'Beratung starten';
    btn.style.opacity = '1';
    hint.textContent = error.message;
    // Loader-Zustand zurücksetzen
    startAvatar.classList.remove('loading');
    $('startScreen').classList.remove('loading');
    startTitle.textContent = 'Hallo! Ich bin Brian.';
    startSubtitle.textContent = 'Frag mich alles rund um Kleben, Dichten & Reparieren. Ich helfe dir das richtige Dekalin-Produkt zu finden.';
  }
}

// ================================================================
// Send User Text
// ================================================================

function sendUserText(prefilledText?: string) {
  const input = $('userTextInput') as HTMLInputElement;
  const text = prefilledText || input.value.trim();
  if (!text || !session) return;

  addChat(text, 'user');
  session.sendText(text);
  logger?.addTurn('user', text, { input_mode: 'text' });
  if (!prefilledText) input.value = '';
  log('info', `User-Text gesendet: "${text}"`);

  // Sofort Typing-Indikator anzeigen — wird durch addChat(type='avatar') entfernt
  showTypingIndicator();
}

// ================================================================
// Global Bindings (für onclick in HTML)
// ================================================================

declare global {
  interface Window {
    __startSession: () => void;
    __sendUserText: () => void;
    __switchTab: (name: string) => void;
    __submitHandoff: (e: Event) => void;
    __cancelHandoff: () => void;
    __toggleMicMute: () => void;
    __toggleSpeakerMute: () => void;
    __interruptAvatar: () => void;
    __endSession: () => void;
  }
}

window.__startSession = startSession;
window.__sendUserText = sendUserText;
window.__switchTab = switchTab;
window.__submitHandoff = submitHandoff;
window.__cancelHandoff = cancelHandoff;
window.__toggleMicMute = toggleMicMute;
window.__toggleSpeakerMute = toggleSpeakerMute;
window.__interruptAvatar = interruptAvatar;
window.__endSession = endSession;

// ================================================================
// Init
// ================================================================

log('info', 'Dekalin Demo geladen');
log('info', 'SDK: @heygen/liveavatar-web-sdk v0.0.9');
log('info', 'Klicke "Session starten" um zu beginnen');
